<!--
  UploadTray.vue

  The upload queue as a toast (useUploadToast shows it): one row per file with
  its progress, where it's headed, and Retry when it failed. Living in the
  toaster puts it beside the app's other notices and above open dialogs.
-->
<template>
  <aside class="upload-tray b-1 r-3" role="status" aria-label="Uploads">
    <header>
      <span class="grow font-semibold">{{ heading }}</span>
      <span v-if="active.length" class="text-7 text-muted">{{ remaining }} left</span>
      <button
        class="btn btn-sm is-transparent"
        :aria-label="collapsed ? 'Show uploads' : 'Hide uploads'"
        @click="collapsed = !collapsed"
      >
        <font-awesome-icon :icon="collapsed ? 'chevron-up' : 'chevron-down'" />
      </button>
      <button
        v-if="!active.length"
        class="btn btn-sm is-transparent"
        aria-label="Clear uploads"
        @click="clearFinished"
      >
        <font-awesome-icon icon="xmark" />
      </button>
    </header>
    <ul v-if="!collapsed">
      <li v-for="upload in uploads" :key="upload.id">
        <div class="flex items-center g-2">
          <span class="grow is-elided text-7 font-medium">{{ upload.file.name }}</span>
          <span class="status text-7" :class="`is-${upload.status}`">{{
            statusLabel(upload)
          }}</span>
        </div>
        <progress class="bar" :class="`is-${upload.status}`" :value="upload.progress" max="100" />
        <div class="flex items-center g-2 text-7 text-muted">
          <span class="grow is-elided">{{
            upload.error ||
            (upload.target.destination ? `→ ${upload.target.destination}` : "Library")
          }}</span>
          <a v-if="upload.status === 'failed' && !upload.refused" @click="retry(upload.id)"
            >Retry</a
          >
          <a
            v-if="upload.status === 'failed' || upload.status === 'done'"
            @click="dismiss(upload.id)"
            >Dismiss</a
          >
        </div>
      </li>
    </ul>
  </aside>
</template>

<script setup lang="ts">
import { computed, ref } from "vue"
import { storeToRefs } from "pinia"
import { formatFileSize } from "@auger/ui/utils/number"
import { type Upload, useUploadsStore } from "@/stores/uploads"

const store = useUploadsStore()
const { uploads, active, failed, remainingBytes } = storeToRefs(store)
const { retry, dismiss, clearFinished } = store

const collapsed = ref(false)

const heading = computed(() => {
  const total = uploads.value.length
  if (active.value.length) return `Uploading ${active.value.length} of ${total}`
  if (failed.value.length) return `${failed.value.length} of ${total} failed`
  return total === 1 ? "Upload complete" : `${total} uploads complete`
})

const remaining = computed(() => formatFileSize(remainingBytes.value, 0))

function statusLabel(upload: Upload) {
  if (upload.status === "uploading") return `${upload.progress}%`
  if (upload.status === "queued") return "Queued"
  if (upload.status === "failed") return "Failed"
  return "Done"
}
</script>

<style>
.upload-tray {
  width: var(--width, 22rem);
  max-height: 50vh;
  display: flex;
  flex-direction: column;
  background: var(--auger-bg);
  box-shadow: var(--auger-shadow-overlay);

  header {
    display: flex;
    align-items: center;
    gap: 0.25rem;
    padding: 0.5rem 0.5rem 0.5rem 0.875rem;
    border-bottom: 1px solid var(--auger-border-light);
  }

  ul {
    overflow-y: auto;
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    display: flex;
    flex-direction: column;
    gap: 0.3rem;
    padding: 0.6rem 0.875rem;
    border-bottom: 1px solid var(--auger-border-light);

    &:last-child {
      border-bottom: 0;
    }
  }

  .bar {
    --fill: var(--auger-primary);
    width: 100%;
    height: 0.25rem;
    border: 0;
    border-radius: 2px;
    overflow: hidden;
    appearance: none;
    background: var(--auger-bg-muted);

    &::-webkit-progress-bar {
      background: var(--auger-bg-muted);
    }

    &::-webkit-progress-value {
      background: var(--fill);
    }

    &::-moz-progress-bar {
      background: var(--fill);
    }

    &.is-done {
      --fill: var(--auger-success);
    }

    &.is-failed {
      --fill: var(--auger-danger);
    }
  }

  .status {
    font-weight: 600;

    &.is-done {
      color: var(--auger-success);
    }

    &.is-failed {
      color: var(--auger-danger);
    }
  }
}
</style>
